import { useEffect, useState } from 'react';
import { api } from '../api/client.js';
import { MORPH_BTN_SM } from '../lib/ui.js';

const ICON_COLORS = {
  'School Safari': { bg: 'color-mix(in oklab, var(--accent) 12%, transparent)', text: 'var(--accent)' },
  'Exotic Expo': { bg: 'rgba(255,199,44,0.12)', text: '#B37800' },
  'Animal Contest': { bg: 'rgba(220,80,60,0.12)', text: '#DC503C' },
  'Wildlife Talk': { bg: 'rgba(80,130,200,0.12)', text: '#3A72B8' },
};
const BULAN = ['', 'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'];

function fmtTanggal(ymd) {
  const [y, m, d] = ymd.split('-');
  return `${parseInt(d, 10)} ${BULAN[parseInt(m, 10)]} ${y}`;
}

// Badge "Baru saja" / "x hari lalu" untuk riwayat yang sudah lewat
function fmtLalu(ymd) {
  const lalu = new Date(`${ymd}T00:00:00`);
  const kini = new Date();
  kini.setHours(0, 0, 0, 0);
  const hari = Math.round((kini - lalu) / 86400000);
  if (hari <= 0) return 'Hari ini';
  if (hari === 1) return 'Kemarin';
  if (hari < 30) return `${hari} hari lalu`;
  const bulan = Math.floor(hari / 30);
  if (bulan < 12) return `${bulan} bulan lalu`;
  return `${Math.floor(bulan / 12)} tahun lalu`;
}

const MODE = {
  upcoming: {
    icon: 'event_available',
    judul: <>Jadwal Event <span style={{ color: 'var(--accent)' }}>Terdekat</span></>,
    ket: 'Lihat jadwal event yang sedang berlangsung atau akan datang dalam 30 hari ke depan.',
    kosongJudul: 'Belum Ada Jadwal',
    kosongKet: 'Belum ada event terjadwal dalam waktu dekat.',
  },
  history: {
    icon: 'history',
    judul: <>Riwayat <span style={{ color: 'var(--accent)' }}>Kegiatan</span></>,
    ket: 'Klien dan institusi yang pernah booking event bersama kami.',
    kosongJudul: 'Belum Ada Riwayat',
    kosongKet: 'Belum ada kegiatan yang sudah terlaksana.',
  },
};

function KartuBooking({ b, i, mode }) {
  const c = ICON_COLORS[b.layanan_nama] || { bg: 'color-mix(in oklab, var(--accent) 12%, transparent)', text: 'var(--accent)' };
  const ikon = mode === 'upcoming' ? 'event_available' : 'task_alt';

  return (
    <div
      className="fade-in-up flex flex-col gap-4 rounded-2xl p-5 border bg-white dark:bg-neutral-900"
      style={{ borderColor: 'color-mix(in oklab, var(--accent) 12%, transparent)', animationDelay: `${Math.min(i, 8) * 0.05}s`, animationFillMode: 'both' }}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: c.bg }}>
          <span className="material-symbols-outlined text-2xl" style={{ color: c.text }}>{ikon}</span>
        </div>
        {b.layanan_nama && (
          <span className="text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: c.bg, color: c.text }}>{b.layanan_nama}</span>
        )}
      </div>
      <div className="flex-1">
        <h4 className="font-bold text-neutral-900 dark:text-white">{b.nama_klien}</h4>
        {b.institusi && <p className="text-sm text-neutral-500 dark:text-neutral-400">{b.institusi}</p>}
      </div>
      <div className="text-sm text-neutral-600 dark:text-neutral-300 font-medium pt-3 border-t" style={{ borderColor: 'color-mix(in oklab, var(--accent) 10%, transparent)' }}>
        <div className="flex items-center gap-2 flex-wrap">
          <span>{fmtTanggal(b.tanggal_acara)}</span>
          {mode === 'history' && (
            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-neutral-100 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400">
              {fmtLalu(b.tanggal_acara)}
            </span>
          )}
        </div>
        <div className="text-neutral-400 text-xs">{b.jam_mulai?.slice(0, 5)} - {b.jam_selesai?.slice(0, 5)} WIB</div>
      </div>
    </div>
  );
}

export default function BookedInfoSection() {
  const [bookings, setBookings] = useState(null);
  const [history, setHistory] = useState(null);
  const [mode, setMode] = useState('upcoming');
  const [view, setView] = useState('list');

  useEffect(() => {
    api.get('/booking').then(({ data }) => setBookings(data.bookings || []));
    api.get('/booking/history').then(({ data }) => setHistory(data.bookings || [])).catch(() => setHistory([]));
  }, []);

  const aktif = mode === 'upcoming' ? bookings : history;
  const data = aktif || [];
  const m = MODE[mode];

  return (
    <section className="py-20 md:py-28">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold mb-4 border" style={{ background: 'color-mix(in oklab, var(--accent) 8%, transparent)', borderColor: 'color-mix(in oklab, var(--accent) 20%, transparent)', color: 'var(--accent)' }}>
            <span className="material-symbols-outlined">{m.icon}</span> JADWAL & INFO
          </div>
          <h2 className="font-display text-3xl md:text-5xl font-bold text-neutral-900 dark:text-white mb-4">
            {m.judul}
          </h2>
          <p className="text-neutral-500 dark:text-neutral-400 max-w-xl mx-auto">
            {m.ket}
          </p>
        </div>

        {/* Tab: Akan Datang / Riwayat */}
        <div className="flex justify-center mb-6">
          <div className="relative inline-flex items-center gap-1 p-1 rounded-full border bg-white dark:bg-neutral-900" style={{ borderColor: 'color-mix(in oklab, var(--accent) 20%, transparent)' }}>
            <div
              className="absolute top-1 bottom-1 left-1 w-[calc(50%-0.375rem)] rounded-full transition-transform duration-300"
              style={{ background: 'var(--accent)', transform: mode === 'history' ? 'translateX(calc(100% + 0.25rem))' : 'translateX(0)', transitionTimingFunction: 'var(--ease-spring)' }}
            />
            <button
              type="button"
              onClick={() => setMode('upcoming')}
              aria-pressed={mode === 'upcoming'}
              className={`relative z-10 flex items-center justify-center gap-1.5 px-4 h-8 rounded-full text-xs font-bold white-space-nowrap ${MORPH_BTN_SM}`}
              style={{ color: mode === 'upcoming' ? '#fff' : 'var(--accent)', transitionProperty: 'color, border-radius, transform' }}
            >
              <span className="material-symbols-outlined text-base">event_upcoming</span>
              Akan Datang
              {bookings?.length > 0 && <span className="opacity-70">({bookings.length})</span>}
            </button>
            <button
              type="button"
              onClick={() => setMode('history')}
              aria-pressed={mode === 'history'}
              className={`relative z-10 flex items-center justify-center gap-1.5 px-4 h-8 rounded-full text-xs font-bold white-space-nowrap ${MORPH_BTN_SM}`}
              style={{ color: mode === 'history' ? '#fff' : 'var(--accent)', transitionProperty: 'color, border-radius, transform' }}
            >
              <span className="material-symbols-outlined text-base">history</span>
              Riwayat
              {history?.length > 0 && <span className="opacity-70">({history.length})</span>}
            </button>
          </div>
        </div>

        {aktif === null ? (
          <div className="text-center py-16 text-neutral-400">{mode === 'upcoming' ? 'Memuat jadwal...' : 'Memuat riwayat...'}</div>
        ) : data.length === 0 ? (
          <div className="text-center py-16">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-6" style={{ background: 'color-mix(in oklab, var(--accent) 10%, transparent)' }}>
              <span className="material-symbols-outlined text-4xl" style={{ color: 'var(--accent)' }}>{mode === 'upcoming' ? 'event_busy' : 'history_toggle_off'}</span>
            </div>
            <h3 className="text-2xl font-bold text-neutral-900 dark:text-white mb-2">{m.kosongJudul}</h3>
            <p className="text-neutral-500 dark:text-neutral-400">{m.kosongKet}</p>
          </div>
        ) : (
          <>
            <div className="flex justify-end mb-6">
              <div className="relative inline-flex items-center gap-1 p-1 rounded-full border bg-white dark:bg-neutral-900" style={{ borderColor: 'color-mix(in oklab, var(--accent) 20%, transparent)' }}>
                <div
                  className="absolute top-1 bottom-1 left-1 w-[calc(50%-0.375rem)] rounded-full transition-transform duration-300"
                  style={{ background: 'var(--accent)', transform: view === 'grid' ? 'translateX(calc(100% + 0.25rem))' : 'translateX(0)', transitionTimingFunction: 'var(--ease-spring)' }}
                />
                <button
                  type="button"
                  onClick={() => setView('list')}
                  aria-pressed={view === 'list'}
                  className={`relative z-10 flex-1 flex items-center justify-center gap-1.5 px-3.5 h-8 rounded-full text-xs font-bold ${MORPH_BTN_SM}`}
                  style={{ color: view === 'list' ? '#fff' : 'var(--accent)', transitionProperty: 'color, border-radius, transform' }}
                >
                  <span className="material-symbols-outlined text-base">view_list</span>
                  List
                </button>
                <button
                  type="button"
                  onClick={() => setView('grid')}
                  aria-pressed={view === 'grid'}
                  className={`relative z-10 flex-1 flex items-center justify-center gap-1.5 px-3.5 h-8 rounded-full text-xs font-bold ${MORPH_BTN_SM}`}
                  style={{ color: view === 'grid' ? '#fff' : 'var(--accent)', transitionProperty: 'color, border-radius, transform' }}
                >
                  <span className="material-symbols-outlined text-base">grid_view</span>
                  Grid
                </button>
              </div>
            </div>

            {view === 'list' ? (
              <div className="space-y-4">
                {data.map((b, i) => {
                  const c = ICON_COLORS[b.layanan_nama] || { bg: 'color-mix(in oklab, var(--accent) 12%, transparent)', text: 'var(--accent)' };
                  return (
                    <div key={`list-${mode}-${b.id}`} className="fade-in-up flex flex-col sm:flex-row sm:items-center gap-4 rounded-2xl p-5 border bg-white dark:bg-neutral-900" style={{ borderColor: 'color-mix(in oklab, var(--accent) 12%, transparent)', animationDelay: `${Math.min(i, 8) * 0.05}s`, animationFillMode: 'both' }}>
                      <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: c.bg }}>
                        <span className="material-symbols-outlined text-2xl" style={{ color: c.text }}>{mode === 'upcoming' ? 'event_available' : 'task_alt'}</span>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-bold text-neutral-900 dark:text-white">{b.nama_klien}</h4>
                          {b.layanan_nama && (
                            <span className="text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: c.bg, color: c.text }}>{b.layanan_nama}</span>
                          )}
                          {mode === 'history' && (
                            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-neutral-100 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400">
                              {fmtLalu(b.tanggal_acara)}
                            </span>
                          )}
                        </div>
                        {b.institusi && <p className="text-sm text-neutral-500 dark:text-neutral-400">{b.institusi}</p>}
                      </div>
                      <div className="text-sm text-neutral-600 dark:text-neutral-300 font-medium sm:text-right">
                        <div>{fmtTanggal(b.tanggal_acara)}</div>
                        <div className="text-neutral-400 text-xs">{b.jam_mulai?.slice(0, 5)} - {b.jam_selesai?.slice(0, 5)} WIB</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {data.map((b, i) => <KartuBooking key={`grid-${mode}-${b.id}`} b={b} i={i} mode={mode} />)}
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}
